import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppContextService, EcaGenericMessageDialogConfig, EcaGenericMessageDialogComponent, EcaPillarBoxModule, EcaFormColumnModule, EcaSpinner } from '@drc-eca/eca-components-lib';
import { PortalError } from '../../shared/portal-error.model';
import { UserManagementService } from '../../user/services/user-management.service';
import { UserSecurityService } from '../../user/services/user-security.service';
import { NewPasswordComponent } from '../new-password/new-password.component';
import { ProductLoginService } from '../services/product-login.service';
import { DefaultLayoutDirective, DefaultLayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    imports: [
      NewPasswordComponent,
      RouterLink,
      EcaPillarBoxModule,
      EcaFormColumnModule,
      DefaultLayoutDirective,
      DefaultLayoutAlignDirective,
      AsyncPipe,
      EcaSpinner
    ]
})
export class ResetPasswordComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private userSecurityService = inject(UserSecurityService);
  private userManagementService = inject(UserManagementService);
  private appContextService = inject(AppContextService);
  private dialog = inject(MatDialog);
  private productLoginService = inject(ProductLoginService);


  temporaryToken: string;
  userId: string;
  isLoading = new BehaviorSubject(true);
  isLoading$ = this.isLoading.asObservable();
  productCode: string;
  userEmail: string;
  requestedNewLink = false;
  islandDisabled = false;

  ngOnInit() {
    this.appContextService.setSelectedProductCode('');

    this.activatedRoute.queryParams.subscribe(params => {
      this.temporaryToken = params['token'].replace(/(\r\n|\n|\r)/gm, '');
      this.userId = (jwtDecode(this.temporaryToken) as any).sub;
      this.productCode = params['productCode'];
      this.userEmail = (jwtDecode(this.temporaryToken) as any).username;
      if (this.productCode) {
        this.appContextService.setSelectedProductCode(this.productCode);
      }
    });
  }

  openGoodModal(inMessage: string) {
    const data: EcaGenericMessageDialogConfig = {
      title: 'Success',
      message: `<p>${inMessage}</p>`,
    };
    this.dialog.open(EcaGenericMessageDialogComponent, { data });
  }

  openBadModal(err: string) {
    const data: EcaGenericMessageDialogConfig = {
      title: 'Password Not Accepted',
      icon: 'warning-triangle',
      message: `<p>${err}</p>`,
    };
    this.dialog.open(EcaGenericMessageDialogComponent, { data });
  }

  openDialog(err: PortalError) {
    const data: EcaGenericMessageDialogConfig = {
      message: err.friendlyErrorMessage,
      title: 'Expired Password Reset Link',
      icon: 'warning-triangle',
      buttons: {
        negative: {
          label: 'Cancel'
        },
        positive: {
          label: 'Send a New Link'
        }
      }
    };

    const dialog = this.dialog.open(EcaGenericMessageDialogComponent, {
      width: '600px',
      panelClass: 'activate-account-dialog',
      data: data
    })
      dialog.beforeClosed()
      .pipe(take(1))
      .subscribe((sendLink) => {
        if (sendLink) {
          this.resendEmail();
        }
      });
  }

  resendEmail() {
    this.loading(true);
    this.userManagementService.forgotPassword(this.userEmail, this.productCode)
      .subscribe({
        next: () => {
          this.loading(false);
          this.requestedNewLink = true;
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        error: (err) => {
          this.loading(false);
          this.openBadModal('There was a problem sending the email.');
        }
      });
  }

  updatePassword(newPassword: string) {
    const pswdObject = {
      password: newPassword,
      token: this.temporaryToken
    };
    this.userSecurityService.updateWithNewPassword(this.userId, pswdObject).subscribe({
      next: (user) => {
        this.appContextService.setToken(user.token);
        const resetSuccessText = 'Your password has been successfully changed.';
        this.userSecurityService.getUserForToken(user.token).subscribe({
          next: () => {
            this.appContextService.setUserId(user.uid);
            this.openGoodModal(resetSuccessText);
            if (this.productCode) {
              this.productLoginService.notifyProductLogin(this.userId, this.userEmail, this.productCode).subscribe();
            }
            this.router.navigate(['/products']);
          },
          error: (e) => {
            if (e.error?.code === 'island') {
              this.loading(false);
              this.openGoodModal(`${resetSuccessText} Please login through the Island Browser.`);
              this.islandDisabled = true;
            } else {
              this.openBadModal(`Failed to get user for token, error: ${e}`);
            }
          }
        });
      },
      error: (err: PortalError) => {
        this.loading(false);
        if (err.status === 403) {
          this.openDialog(err);
        } else {
          const friendlyMessage = err.friendlyErrorMessage + ' Please request a new password reset token.';
          this.openBadModal(friendlyMessage);
        }
      }
    });
  }

  loading(event: boolean) {
    this.isLoading.next(event);
  }

}
